import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { toast } from "sonner";
import { PenLine, BadgeCheck, Users, Sparkles, ArrowRight } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const PERKS = [
  { icon: BadgeCheck, title: "Verified byline", desc: "Your name on every story plus a dedicated author profile page readers can follow." },
  { icon: Users, title: "Built-in audience", desc: "Publish to NewsLayer's daily crypto, Web3 and policy readership — no cold start." },
  { icon: Sparkles, title: "Editorial polish", desc: "Every submission is reviewed by our editors before it goes live, protecting your credibility." },
];

export default function AuthorProgram() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ display_name: "", bio: "", expertise: "" });
  const [paying, setPaying] = useState(false);

  const { data } = useQuery({
    queryKey: ["author-program-me"],
    queryFn: async () => (await api.get("/author-program/me")).data,
    enabled: !!user,
  });

  const application = data?.application;
  const status = data?.author_status || application?.status;

  const join = async () => {
    if (!user) { navigate("/login"); return; }
    setPaying(true);
    try {
      if (!application || application.status === "draft_unpaid") {
        await api.post("/author-program/apply", {
          display_name: form.display_name || user.full_name,
          bio: form.bio || "Crypto writer joining the NewsLayer Approved Author program.",
          expertise: form.expertise.split(",").map((s) => s.trim()).filter(Boolean),
        });
      }
      const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/payments/checkout`,
        { lookup_key: "author_annual", origin_url: window.location.origin },
        { headers: { Authorization: `Bearer ${localStorage.getItem("nl_token")}` }, withCredentials: true });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(apiError(e));
      setPaying(false);
    }
  };

  return (
    <div data-testid="author-program-page" className="max-w-5xl mx-auto px-4 sm:px-6 py-14">
      <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.3em] text-[#7C3AED] mb-4">
        <PenLine className="h-4 w-4" /> Approved Author Program
      </div>
      <h1 className="font-heading font-black text-white text-3xl sm:text-5xl leading-tight max-w-3xl">
        Write for NewsLayer. Get read by the people who move crypto.
      </h1>
      <p className="text-gray-400 mt-5 max-w-2xl text-base sm:text-lg leading-relaxed">
        Join for <span className="text-white font-bold">$99/year</span> and publish your analysis,
        opinion and reporting under your own verified byline — with editorial review on every story.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-12">
        {PERKS.map((p) => (
          <div key={p.title} className="bg-[#121212] border border-white/10 rounded-xl p-6 hover:border-[#7C3AED]/50 transition-colors">
            <p.icon className="h-7 w-7 text-[#7C3AED] mb-4" />
            <h3 className="font-heading font-bold text-white text-lg">{p.title}</h3>
            <p className="text-sm text-gray-400 mt-2 leading-relaxed">{p.desc}</p>
          </div>
        ))}
      </div>

      <div className="mt-12 bg-[#0d0d14] border border-[#7C3AED]/30 rounded-xl p-6 sm:p-10">
        {status === "approved" && (
          <div data-testid="author-approved-state">
            <h2 className="font-heading font-bold text-white text-2xl mb-2">You're an Approved Author ✓</h2>
            <p className="text-gray-400 mb-6">Head to your studio to write and submit stories.</p>
            <Button onClick={() => navigate("/studio")} data-testid="go-to-studio-button"
              className="bg-[#7C3AED] text-white font-bold hover:bg-[#6a2dd0]">
              Open Author Studio <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
          </div>
        )}
        {(status === "pending" || status === "pending_review") && status !== "approved" && (
          <div data-testid="author-pending-state">
            <h2 className="font-heading font-bold text-white text-2xl mb-2">Application under review</h2>
            <p className="text-gray-400">Payment received — our team is reviewing your application. You'll get access to the Author Studio as soon as you're approved.</p>
          </div>
        )}
        {status === "rejected" && (
          <div data-testid="author-rejected-state">
            <h2 className="font-heading font-bold text-white text-2xl mb-2">Application not approved</h2>
            <p className="text-gray-400">Your application wasn't approved this time. Contact us for details or re-apply below.</p>
          </div>
        )}
        {(!status || status === "draft_unpaid" || status === "rejected") && (
          <div className="mt-2">
            <h2 className="font-heading font-bold text-white text-2xl mb-6">Apply & join for $99/yr</h2>
            {!user ? (
              <div>
                <p className="text-gray-400 mb-4">Sign in or create a free account to apply.</p>
                <Button onClick={() => navigate("/login")} data-testid="author-signin-button"
                  className="bg-[#00E5FF] text-black font-bold hover:bg-[#00c9e0]">Sign in to continue</Button>
              </div>
            ) : (
              <div className="space-y-4 max-w-xl">
                <div>
                  <Label>Pen name / byline</Label>
                  <Input value={form.display_name} placeholder={user.full_name}
                    onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                    data-testid="author-form-name" className="mt-1 bg-[#1A1A1A] border-white/10 text-white" />
                </div>
                <div>
                  <Label>Short bio (shown on your profile)</Label>
                  <Textarea value={form.bio} rows={3}
                    placeholder="10 years covering DeFi protocols and on-chain forensics…"
                    onChange={(e) => setForm({ ...form, bio: e.target.value })}
                    data-testid="author-form-bio" className="mt-1 bg-[#1A1A1A] border-white/10 text-white" />
                </div>
                <div>
                  <Label>Expertise (comma separated)</Label>
                  <Input value={form.expertise} placeholder="DeFi, regulation, markets"
                    onChange={(e) => setForm({ ...form, expertise: e.target.value })}
                    data-testid="author-form-expertise" className="mt-1 bg-[#1A1A1A] border-white/10 text-white" />
                </div>
                <Button onClick={join} disabled={paying || form.bio.trim().length < 20}
                  data-testid="author-join-button"
                  className="bg-[#7C3AED] text-white font-bold hover:bg-[#6a2dd0] w-full sm:w-auto px-8">
                  {paying ? "Redirecting to checkout…" : "Join for $99/yr — secure checkout"}
                </Button>
                {form.bio.trim().length < 20 && (
                  <p className="font-mono text-[10px] text-gray-500">Add a short bio (20+ characters) to continue.</p>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <p className="font-mono text-[10px] text-gray-600 mt-6">
        Payments processed securely by Stripe. Every story is subject to editorial approval before publication.{" "}
        <Link to="/advertise" className="text-gray-500 underline">Looking to advertise instead?</Link>
      </p>
    </div>
  );
}
